import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const ADMIN_PASSWORD = "fortune2024";

interface Submission {
  id: string;
  name: string;
  gender: string;
  birthday: string;
  concern: string | null;
  created_at: string;
}

interface Order {
  id: string;
  order_no: string;
  name: string;
  gender: string;
  birthday: string;
  amount: number;
  pay_type: string;
  status: string;
  paid_at: string | null;
  created_at: string;
}

interface PaymentConfig {
  id: number;
  amount: number;
  description: string;
}

interface WechatWorkConfig {
  id: number;
  corp_id: string;
  corp_secret: string;
  agent_id: string;
  event_type: string;
  token: string;
}

interface KuaishouAdsConfig {
  id: number;
  account_id: string;
  token: string;
  callback_url: string;
  event_type: string;
  enabled: boolean;
}


export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState<Submission | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [activeTab, setActiveTab] = useState<"orders" | "records" | "config" | "keys" | "wechat_work" | "kuaishou">("orders");
  const [payConfig, setPayConfig] = useState<PaymentConfig>({ id: 1, amount: 9800, description: "运势测算服务费" });
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState("");

  // 易支付配置
  const [yiConf, setYiConf] = useState({ pay_url: "", pid: "", key: "", pay_type: "wxpay", return_url: "", wechat_link: "" });
  const [yiLoading, setYiLoading] = useState(false);
  const [yiSaving, setYiSaving] = useState(false);
  const [yiMsg, setYiMsg] = useState("");
  const [showKey, setShowKey] = useState(false);

  // 企业微信配置
  const [wechatWorkConf, setWechatWorkConf] = useState<WechatWorkConfig>({ id: 1, corp_id: "", corp_secret: "", agent_id: "", event_type: "purchase", token: "" });
  const [wechatWorkLoading, setWechatWorkLoading] = useState(false);
  const [wechatWorkSaving, setWechatWorkSaving] = useState(false);
  const [wechatWorkMsg, setWechatWorkMsg] = useState("");
  const [showCorpSecret, setShowCorpSecret] = useState(false);

  // 快手广告配置
  const [kuaishouConf, setKuaishouConf] = useState<KuaishouAdsConfig>({ id: 1, account_id: "", token: "", callback_url: "", event_type: "PURCHASE", enabled: true });
  const [kuaishouLoading, setKuaishouLoading] = useState(false);
  const [kuaishouSaving, setKuaishouSaving] = useState(false);
  const [kuaishouMsg, setKuaishouMsg] = useState("");
  const [showKuaishouToken, setShowKuaishouToken] = useState(false);



  useEffect(() => {
    const saved = sessionStorage.getItem("admin_auth");
    if (saved === "true") setIsLoggedIn(true);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
      fetchOrders();
      fetchPayConfig();
      fetchYiConf();
      fetchWechatWorkConf();
      fetchKuaishouConf();
    }
  }, [isLoggedIn]);



  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("fortune_submissions")
      .select("id, name, gender, birthday, concern, created_at")
      .order("created_at", { ascending: false });
    if (!error && data) setSubmissions(data as Submission[]);
    setLoading(false);
  };

  const fetchOrders = async () => {
    const { data } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setOrders(data as Order[]);
  };

  const fetchPayConfig = async () => {
    const { data } = await supabase
      .from("payment_config")
      .select("*")
      .order("id", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setPayConfig(data as PaymentConfig);
  };



  const fetchYiConf = async () => {
    setYiLoading(true);
    const { data } = await supabase.from("yipay_config").select("*").eq("id", 1).maybeSingle();
    if (data) setYiConf({ pay_url: data.pay_url || "", pid: data.pid || "", key: data.key || "", pay_type: data.pay_type || "wxpay", return_url: data.return_url || "", wechat_link: data.wechat_link || "" });
    setYiLoading(false);
  };

  const fetchWechatWorkConf = async () => {
    setWechatWorkLoading(true);
    const { data } = await supabase.from("wechat_work_config").select("*").eq("id", 1).maybeSingle();
    if (data) setWechatWorkConf({ id: data.id, corp_id: data.corp_id || "", corp_secret: data.corp_secret || "", agent_id: data.agent_id || "", event_type: data.event_type || "purchase", token: data.token || "" });
    setWechatWorkLoading(false);
  };

  const fetchKuaishouConf = async () => {
    setKuaishouLoading(true);
    const { data } = await supabase.from("kuaishou_ads_config").select("*").eq("id", 1).maybeSingle();
    if (data) setKuaishouConf({ id: data.id, account_id: data.account_id || "", token: data.token || "", callback_url: data.callback_url || "", event_type: data.event_type || "PURCHASE", enabled: data.enabled ?? true });
    setKuaishouLoading(false);
  };

  const saveYiConf = async () => {
    setYiSaving(true);
    setYiMsg("");
    const { error } = await supabase.from("yipay_config").update({ ...yiConf, updated_at: new Date().toISOString() }).eq("id", 1);
    setYiSaving(false);
    setYiMsg(error ? "保存失败，请重试" : "保存成功！");
    setTimeout(() => setYiMsg(""), 3000);
  };

  const saveWechatWorkConf = async () => {
    setWechatWorkSaving(true);
    setWechatWorkMsg("");
    const { error } = await supabase.from("wechat_work_config").update({ 
      corp_id: wechatWorkConf.corp_id,
      corp_secret: wechatWorkConf.corp_secret,
      agent_id: wechatWorkConf.agent_id,
      event_type: wechatWorkConf.event_type,
      token: wechatWorkConf.token,
      updated_at: new Date().toISOString()
    }).eq("id", 1);
    setWechatWorkSaving(false);
    setWechatWorkMsg(error ? "保存失败，请重试" : "保存成功！");
    setTimeout(() => setWechatWorkMsg(""), 3000);
  };

  const saveKuaishouConf = async () => {
    setKuaishouSaving(true);
    setKuaishouMsg("");
    const { error } = await supabase.from("kuaishou_ads_config").update({ 
      account_id: kuaishouConf.account_id,
      token: kuaishouConf.token,
      callback_url: kuaishouConf.callback_url,
      event_type: kuaishouConf.event_type,
      enabled: kuaishouConf.enabled,
      updated_at: new Date().toISOString()
    }).eq("id", 1);
    setKuaishouSaving(false);
    setKuaishouMsg(error ? "保存失败，请重试" : "保存成功！");
    setTimeout(() => setKuaishouMsg(""), 3000);
  };

  const savePayConfig = async () => {
    setConfigSaving(true);
    setConfigMsg("");
    const { error } = await supabase
      .from("payment_config")
      .update({ amount: payConfig.amount, description: payConfig.description, updated_at: new Date().toISOString() })
      .eq("id", payConfig.id);
    setConfigSaving(false);
    setConfigMsg(error ? "保存失败，请重试" : "保存成功！");
    setTimeout(() => setConfigMsg(""), 3000);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("admin_auth", "true");
      setIsLoggedIn(true);
      setLoginError("");
    } else {
      setLoginError("密码错误，请重试");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    setIsLoggedIn(false);
    setPassword("");
    setSubmissions([]);
    setOrders([]);
  };

  const handleRefresh = () => {
    fetchData();
    fetchOrders();
  };

  const filteredSubmissions = submissions.filter((s) => {
    const q = search.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.gender?.includes(q);
  });

  const filteredOrders = orders.filter((o) => {
    const q = search.toLowerCase();
    return o.name?.toLowerCase().includes(q) || o.order_no?.includes(q) || o.gender?.includes(q);
  });

  const formatDate = (str: string) => {
    if (!str) return "—";
    const d = new Date(str);
    return d.toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const todayPaid = orders.filter(o => o.status === "paid" && new Date(o.created_at).toDateString() === new Date().toDateString()).length;
  const totalPaid = orders.filter(o => o.status === "paid").length;

  const inputStyle = {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(200,146,42,0.4)",
    color: "#f5e6c8",
    fontFamily: "'Noto Serif SC', serif",
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const isPaid = status === "paid";
    return (
      <span
        className="px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{
          background: isPaid ? "rgba(39,174,96,0.15)" : "rgba(200,146,42,0.15)",
          color: isPaid ? "#6ee7b7" : "#e8b84b",
          border: `1px solid ${isPaid ? "rgba(39,174,96,0.4)" : "rgba(200,146,42,0.3)"}`,
        }}
      >
        {isPaid ? "✓ 已支付" : "待支付"}
      </span>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(160deg, #1a0000 0%, #4a0e0e 30%, #6b1a1a 60%, #3d0000 100%)", fontFamily: "'Noto Serif SC', serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Noto Serif+SC:wght@400;600;700&display=swap');`}</style>
        <div className="w-full max-w-sm mx-4 rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)", border: "3px solid #c8922a" }}>
          <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
          <div className="px-8 py-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full" style={{ background: "rgba(200,146,42,0.15)", border: "2px solid #c8922a" }}>
                <i className="ri-lock-line text-2xl" style={{ color: "#c8922a" }} />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: "#8b1a1a" }}>后台管理</h1>
              <p className="text-sm mt-1" style={{ color: "#7a5c2e" }}>请输入管理员密码</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="请输入密码" className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={{ background: "rgba(255,255,255,0.8)", border: "1.5px solid #c8922a", color: "#4a3000", fontFamily: "'Noto Serif SC', serif" }} />
                {loginError && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{loginError}</p>}
              </div>
              <button type="submit" className="w-full py-3 rounded-xl text-base font-bold tracking-widest cursor-pointer whitespace-nowrap" style={{ background: "linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)", color: "#fff", fontFamily: "'Noto Serif SC', serif" }}>登录</button>
            </form>
          </div>
          <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg, #1a0000 0%, #4a0e0e 30%, #6b1a1a 60%, #3d0000 100%)", fontFamily: "'Noto Serif SC', serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Noto Serif+SC:wght@400;600;700&display=swap');`}</style>

      {/* Top nav */}
      <div className="sticky top-0 z-30 px-6 py-4 flex items-center justify-between" style={{ background: "rgba(26,0,0,0.95)", borderBottom: "1px solid rgba(200,146,42,0.3)" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(200,146,42,0.2)" }}>
            <i className="ri-dashboard-line text-sm" style={{ color: "#e8b84b" }} />
          </div>
          <span className="text-lg font-bold" style={{ color: "#e8b84b" }}>运势测算 · 后台管理</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap" style={{ background: "rgba(200,146,42,0.15)", border: "1px solid rgba(200,146,42,0.4)", color: "#e8b84b" }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line" /></div>
            刷新
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap" style={{ background: "rgba(192,57,43,0.15)", border: "1px solid rgba(192,57,43,0.4)", color: "#e74c3c" }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-line" /></div>
            退出
          </button>
        </div>
      </div>

      <div className="px-6 py-6 max-w-7xl mx-auto">
        {/* 统计卡片 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "总提交数", value: submissions.length, icon: "ri-file-list-3-line", color: "#e8b84b" },
            { label: "总订单数", value: orders.length, icon: "ri-shopping-cart-line", color: "#e8b84b" },
            { label: "已支付", value: totalPaid, icon: "ri-checkbox-circle-line", color: "#6ee7b7" },
            { label: "今日支付", value: todayPaid, icon: "ri-calendar-check-line", color: "#6ee7b7" },
          ].map((stat, i) => (
            <div key={i} className="rounded-xl px-4 py-4" style={{ background: "rgba(253,243,227,0.06)", border: "1px solid rgba(200,146,42,0.25)" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: "rgba(200,146,42,0.15)" }}>
                  <i className={`${stat.icon} text-base`} style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#d4a96a" }}>{stat.label}</p>
                  <p className="text-xl font-bold" style={{ color: "#fff" }}>{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          {[
            { key: "orders", label: "支付订单", icon: "ri-shopping-cart-line" },
            { key: "records", label: "提交记录", icon: "ri-file-list-3-line" },
            { key: "config", label: "支付金额", icon: "ri-money-cny-circle-line" },
            { key: "keys", label: "密钥配置", icon: "ri-key-2-line" },
            { key: "wechat_work", label: "企业微信", icon: "ri-wechat-line" },
            { key: "kuaishou", label: "快手广告", icon: "ri-video-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as "orders" | "records" | "config" | "keys" | "wechat_work" | "kuaishou")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: activeTab === tab.key ? "rgba(200,146,42,0.25)" : "rgba(255,255,255,0.05)",
                border: `1px solid ${activeTab === tab.key ? "rgba(200,146,42,0.6)" : "rgba(200,146,42,0.2)"}`,
                color: activeTab === tab.key ? "#e8b84b" : "#d4a96a",
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={tab.icon} /></div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* 搜索框（订单和记录 tab 共用） */}
        {(activeTab === "orders" || activeTab === "records") && (
          <div className="mb-4 flex items-center gap-3">
            <div className="flex items-center gap-2 flex-1 max-w-sm rounded-lg px-4 py-2" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(200,146,42,0.3)" }}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm" style={{ color: "#e8b84b" }} />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索姓名、订单号..."
                className="flex-1 bg-transparent outline-none text-sm"
                style={{ color: "#f5e6c8", fontFamily: "'Noto Serif SC', serif" }}
              />
            </div>
          </div>
        )}

        {/* ── 支付订单 Tab ── */}
        {activeTab === "orders" && (
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(200,146,42,0.25)" }}>
            {orders.length === 0 ? (
              <div className="py-20 text-center" style={{ color: "#d4a96a" }}>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-4xl" style={{ color: "#c8922a" }} />
                </div>
                <p>暂无订单</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: "rgba(200,146,42,0.15)", borderBottom: "1px solid rgba(200,146,42,0.3)" }}>
                      {["#", "姓名", "性别", "生日", "金额", "支付状态", "提交时间", "支付时间"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: "#e8b84b" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((row, idx) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedOrder(row)}
                        className="cursor-pointer transition-all"
                        style={{ borderBottom: "1px solid rgba(200,146,42,0.1)", background: idx % 2 === 0 ? "rgba(253,243,227,0.03)" : "rgba(253,243,227,0.06)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(200,146,42,0.12)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "rgba(253,243,227,0.03)" : "rgba(253,243,227,0.06)")}
                      >
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "#7a5c2e" }}>{idx + 1}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold" style={{ color: "#f5e6c8" }}>{row.name || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: row.gender === "男" ? "rgba(59,130,246,0.15)" : "rgba(236,72,153,0.15)", color: row.gender === "男" ? "#93c5fd" : "#f9a8d4", border: `1px solid ${row.gender === "男" ? "rgba(59,130,246,0.3)" : "rgba(236,72,153,0.3)"}` }}>
                            {row.gender || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "#d4a96a" }}>{row.birthday || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold" style={{ color: "#e8b84b" }}>¥{row.amount?.toFixed(2)}</td>
                        <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={row.status} /></td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: "#7a5c2e" }}>{formatDate(row.created_at)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: row.paid_at ? "#6ee7b7" : "#5a4020" }}>{row.paid_at ? formatDate(row.paid_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── 提交记录 Tab ── */}
        {activeTab === "records" && (
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(200,146,42,0.25)" }}>
            {loading ? (
              <div className="py-20 text-center" style={{ color: "#d4a96a" }}>
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: "#e8b84b" }} />
                </div>
                <p>加载中...</p>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="py-20 text-center" style={{ color: "#d4a96a" }}>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-4xl" style={{ color: "#c8922a" }} />
                </div>
                <p>暂无数据</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: "rgba(200,146,42,0.15)", borderBottom: "1px solid rgba(200,146,42,0.3)" }}>
                      {["#", "姓名", "性别", "生日", "提交时间"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: "#e8b84b" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((row, idx) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedRow(row)}
                        className="cursor-pointer transition-all"
                        style={{ borderBottom: "1px solid rgba(200,146,42,0.1)", background: idx % 2 === 0 ? "rgba(253,243,227,0.03)" : "rgba(253,243,227,0.06)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(200,146,42,0.12)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "rgba(253,243,227,0.03)" : "rgba(253,243,227,0.06)")}
                      >
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "#7a5c2e" }}>{idx + 1}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold" style={{ color: "#f5e6c8" }}>{row.name || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: row.gender === "男" ? "rgba(59,130,246,0.15)" : "rgba(236,72,153,0.15)", color: row.gender === "男" ? "#93c5fd" : "#f9a8d4", border: `1px solid ${row.gender === "男" ? "rgba(59,130,246,0.3)" : "rgba(236,72,153,0.3)"}` }}>
                            {row.gender}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "#d4a96a" }}>{row.birthday}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: "#7a5c2e" }}>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── 支付金额配置 Tab ── */}
        {activeTab === "config" && (
          <div className="rounded-xl p-6 max-w-lg" style={{ background: "rgba(253,243,227,0.06)", border: "1px solid rgba(200,146,42,0.25)" }}>
            <h2 className="text-base font-bold mb-5" style={{ color: "#e8b84b" }}>支付金额配置</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: "#d4a96a" }}>收费金额（单位：元，如填 9.9 就是收 9.9 元）</label>
                <input type="number" step="0.01" min="0.01" value={payConfig.amount} onChange={(e) => setPayConfig({ ...payConfig, amount: Number(e.target.value) })} className="w-full rounded-lg px-4 py-2 text-sm outline-none" style={inputStyle} />
                <p className="text-xs mt-1" style={{ color: "#7a5c2e" }}>当前金额：{parseFloat(payConfig.amount.toString()).toFixed(2)} 元</p>
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: "#d4a96a" }}>订单描述</label>
                <input type="text" value={payConfig.description} onChange={(e) => setPayConfig({ ...payConfig, description: e.target.value })} className="w-full rounded-lg px-4 py-2 text-sm outline-none" style={inputStyle} />
              </div>
              <button onClick={savePayConfig} disabled={configSaving} className="px-6 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap" style={{ background: configSaving ? "rgba(200,146,42,0.3)" : "linear-gradient(135deg, #c8922a, #e8b84b)", color: "#fff" }}>
                {configSaving ? "保存中..." : "保存配置"}
              </button>
              {configMsg && <p className="text-sm" style={{ color: configMsg.includes("成功") ? "#27ae60" : "#e74c3c" }}>{configMsg}</p>}
            </div>
          </div>
        )}

        {/* ── 易支付配置 Tab ── */}
        {activeTab === "keys" && (
          <div className="rounded-xl p-6 max-w-2xl" style={{ background: "rgba(253,243,227,0.06)", border: "1px solid rgba(200,146,42,0.25)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: "rgba(200,146,42,0.15)" }}>
                <i className="ri-secure-payment-line" style={{ color: "#e8b84b" }} />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: "#e8b84b" }}>彩虹易支付配置</h2>
                <p className="text-xs mt-0.5" style={{ color: "#7a5c2e" }}>修改后立即生效，无需重新部署</p>
              </div>
            </div>
            {yiLoading ? (
              <div className="py-10 text-center">
                <i className="ri-loader-4-line text-xl animate-spin" style={{ color: "#e8b84b" }} />
                <p className="text-sm mt-2" style={{ color: "#d4a96a" }}>加载中...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* 接口地址 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    支付接口地址 <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={yiConf.pay_url}
                    onChange={(e) => setYiConf({ ...yiConf, pay_url: e.target.value })}
                    placeholder="https://你的易支付域名"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                </div>
                {/* PID */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    商户 ID（PID）<span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={yiConf.pid}
                    onChange={(e) => setYiConf({ ...yiConf, pid: e.target.value })}
                    placeholder="如：1001"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                </div>
                {/* 密钥 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    商户 MD5 密钥 <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showKey ? "text" : "password"}
                      value={yiConf.key}
                      onChange={(e) => setYiConf({ ...yiConf, key: e.target.value })}
                      placeholder="请输入 MD5 密钥"
                      className="w-full rounded-lg px-4 py-2.5 text-sm outline-none pr-10"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer"
                    >
                      <i className={showKey ? "ri-eye-off-line" : "ri-eye-line"} style={{ color: "#e8b84b", fontSize: "14px" }} />
                    </button>
                  </div>
                </div>
                {/* 支付方式 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>支付方式</label>
                  <div className="flex gap-3">
                    {[{ val: "wxpay", label: "微信支付" }, { val: "alipay", label: "支付宝" }].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setYiConf({ ...yiConf, pay_type: opt.val })}
                        className="flex-1 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                        style={{
                          border: "1.5px solid rgba(200,146,42,0.4)",
                          background: yiConf.pay_type === opt.val ? "linear-gradient(135deg, #c8922a, #e8b84b)" : "rgba(255,255,255,0.05)",
                          color: yiConf.pay_type === opt.val ? "#fff" : "#d4a96a",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* 支付成功跳转地址 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>支付成功跳转地址</label>
                  <input
                    type="text"
                    value={yiConf.return_url}
                    onChange={(e) => setYiConf({ ...yiConf, return_url: e.target.value })}
                    placeholder="https://你的域名/success（留空则跳回首页）"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                </div>
                {/* 企业微信获客链接 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    企业微信获客链接 <span className="text-xs" style={{ color: "#7a5c2e" }}>（支付成功后跳转）</span>
                  </label>
                  <input
                    type="text"
                    value={yiConf.wechat_link}
                    onChange={(e) => setYiConf({ ...yiConf, wechat_link: e.target.value })}
                    placeholder="https://work.weixin.qq.com/ca/cawc..."
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>用户支付成功后将自动跳转到此链接，用于添加企业微信</p>
                </div>

                {/* 回调地址（只读展示） */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>异步回调地址（填入易支付后台）</label>
                  <div className="rounded-lg px-4 py-2.5 text-xs break-all" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,146,42,0.2)", color: "#9a7a5a", fontFamily: "monospace" }}>
                    {`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/yipay-notify`}
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>请将此地址填入彩虹易支付后台的异步通知地址</p>
                </div>
                {/* 保存按钮 */}
                <div className="flex items-center gap-4 pt-2">
                  <button
                    onClick={saveYiConf}
                    disabled={yiSaving}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
                    style={{ background: yiSaving ? "rgba(200,146,42,0.3)" : "linear-gradient(135deg, #c8922a, #e8b84b)", color: "#fff" }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      {yiSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                    </div>
                    {yiSaving ? "保存中..." : "保存配置"}
                  </button>
                  {yiMsg && (
                    <p className="text-sm" style={{ color: yiMsg.includes("成功") ? "#27ae60" : "#e74c3c" }}>
                      {yiMsg.includes("成功") ? "✓ " : "✗ "}{yiMsg}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 企业微信获客助手配置 Tab ── */}
        {activeTab === "wechat_work" && (
          <div className="rounded-xl p-6 max-w-2xl" style={{ background: "rgba(253,243,227,0.06)", border: "1px solid rgba(200,146,42,0.25)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: "rgba(7,193,96,0.15)" }}>
                <i className="ri-wechat-line" style={{ color: "#07c160" }} />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: "#e8b84b" }}>企业微信获客助手配置</h2>
                <p className="text-xs mt-0.5" style={{ color: "#7a5c2e" }}>配置后可在用户添加企业微信时上报转化事件</p>
              </div>
            </div>
            {wechatWorkLoading ? (
              <div className="py-10 text-center">
                <i className="ri-loader-4-line text-xl animate-spin" style={{ color: "#e8b84b" }} />
                <p className="text-sm mt-2" style={{ color: "#d4a96a" }}>加载中...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* CorpID */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    企业微信 CorpID <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={wechatWorkConf.corp_id}
                    onChange={(e) => setWechatWorkConf({ ...wechatWorkConf, corp_id: e.target.value })}
                    placeholder="如：wwxxxxxxxxxxxxxxxx"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>在企业微信管理后台「我的企业」页面获取</p>
                </div>
                {/* CorpSecret */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    企业微信 Secret <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCorpSecret ? "text" : "password"}
                      value={wechatWorkConf.corp_secret}
                      onChange={(e) => setWechatWorkConf({ ...wechatWorkConf, corp_secret: e.target.value })}
                      placeholder="请输入企业微信 Secret"
                      className="w-full rounded-lg px-4 py-2.5 text-sm outline-none pr-10"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCorpSecret(!showCorpSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer"
                    >
                      <i className={showCorpSecret ? "ri-eye-off-line" : "ri-eye-line"} style={{ color: "#e8b84b", fontSize: "14px" }} />
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>在企业微信管理后台「应用管理」-「自建应用」中获取</p>
                </div>
                {/* AgentID */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    应用 AgentID
                  </label>
                  <input
                    type="text"
                    value={wechatWorkConf.agent_id}
                    onChange={(e) => setWechatWorkConf({ ...wechatWorkConf, agent_id: e.target.value })}
                    placeholder="如：1000002"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                </div>
                {/* Token */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    回调 Token <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={wechatWorkConf.token}
                    onChange={(e) => setWechatWorkConf({ ...wechatWorkConf, token: e.target.value })}
                    placeholder="用于验证回调URL的Token"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>在企业微信管理后台「应用管理」-「接收消息」-「设置API」中配置</p>
                </div>
                {/* 回调URL */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    回调URL（复制到企业微信后台）
                  </label>
                  <div 
                    className="rounded-lg px-4 py-2.5 text-xs break-all cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,146,42,0.2)", color: "#9a7a5a", fontFamily: "monospace" }}
                    onClick={() => {
                      const url = `${window.location.origin}/wechat-callback`;
                      navigator.clipboard.writeText(url);
                      alert("回调URL已复制到剪贴板");
                    }}
                  >
                    {`${window.location.origin}/wechat-callback`}
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>点击复制，粘贴到企业微信后台「接收消息」-「设置API」的URL配置中</p>
                </div>
                {/* 转化事件类型 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    默认转化事件类型
                  </label>
                  <div className="flex gap-3">
                    {[
                      { val: "open", label: "开口（1）" },
                      { val: "lead", label: "留资（2）" },
                      { val: "purchase", label: "成交（3）" },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setWechatWorkConf({ ...wechatWorkConf, event_type: opt.val })}
                        className="flex-1 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                        style={{
                          border: "1.5px solid rgba(200,146,42,0.4)",
                          background: wechatWorkConf.event_type === opt.val ? "linear-gradient(135deg, #c8922a, #e8b84b)" : "rgba(255,255,255,0.05)",
                          color: wechatWorkConf.event_type === opt.val ? "#fff" : "#d4a96a",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>
                    当前选择：<strong style={{ color: "#e8b84b" }}>
                      {wechatWorkConf.event_type === "purchase" ? "成交" : 
                       wechatWorkConf.event_type === "lead" ? "留资" : "开口"}
                    </strong>
                  </p>
                </div>

                {/* API 说明 */}
                <div className="rounded-lg px-4 py-3 mt-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,146,42,0.2)" }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: "#e8b84b" }}>API 调用说明</p>
                  <p className="text-xs mb-1" style={{ color: "#9a7a5a" }}>Endpoint: <code style={{ color: "#d4a96a" }}>POST /functions/v1/wechat-work-report</code></p>
                  <p className="text-xs mb-1" style={{ color: "#9a7a5a" }}>参数：</p>
                  <ul className="text-xs space-y-1 ml-4" style={{ color: "#9a7a5a" }}>
                    <li>• chat_id: 获客链接ID</li>
                    <li>• external_userid: 客户外部联系人ID</li>
                    <li>• follow_userid: 跟进人ID</li>
                    <li>• event_type: 事件类型（open/lead/purchase）</li>
                    <li>• event_msg: 事件描述</li>
                  </ul>
                </div>

                {/* 保存按钮 */}
                <div className="flex items-center gap-4 pt-2">
                  <button
                    onClick={saveWechatWorkConf}
                    disabled={wechatWorkSaving}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
                    style={{ background: wechatWorkSaving ? "rgba(200,146,42,0.3)" : "linear-gradient(135deg, #c8922a, #e8b84b)", color: "#fff" }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      {wechatWorkSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                    </div>
                    {wechatWorkSaving ? "保存中..." : "保存配置"}
                  </button>
                  {wechatWorkMsg && (
                    <p className="text-sm" style={{ color: wechatWorkMsg.includes("成功") ? "#27ae60" : "#e74c3c" }}>
                      {wechatWorkMsg.includes("成功") ? "✓ " : "✗ "}{wechatWorkMsg}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 快手广告配置 Tab ── */}
        {activeTab === "kuaishou" && (
          <div className="rounded-xl p-6 max-w-2xl" style={{ background: "rgba(253,243,227,0.06)", border: "1px solid rgba(200,146,42,0.25)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: "rgba(255,100,0,0.15)" }}>
                <i className="ri-video-line" style={{ color: "#ff6400" }} />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: "#e8b84b" }}>快手广告转化回传配置</h2>
                <p className="text-xs mt-0.5" style={{ color: "#7a5c2e" }}>配置后可在用户支付成功后自动回传转化事件到快手广告</p>
              </div>
            </div>
            {kuaishouLoading ? (
              <div className="py-10 text-center">
                <i className="ri-loader-4-line text-xl animate-spin" style={{ color: "#e8b84b" }} />
                <p className="text-sm mt-2" style={{ color: "#d4a96a" }}>加载中...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* 启用开关 */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold" style={{ color: "#d4a96a" }}>
                    启用快手广告回传
                  </label>
                  <button
                    type="button"
                    onClick={() => setKuaishouConf({ ...kuaishouConf, enabled: !kuaishouConf.enabled })}
                    className="relative w-12 h-6 rounded-full transition-all cursor-pointer"
                    style={{
                      background: kuaishouConf.enabled ? "linear-gradient(135deg, #ff6400, #ff8533)" : "rgba(255,255,255,0.1)",
                    }}
                  >
                    <div
                      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                      style={{
                        left: kuaishouConf.enabled ? "26px" : "2px",
                      }}
                    />
                  </button>
                </div>

                {/* 账户ID */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    快手广告账户ID <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={kuaishouConf.account_id}
                    onChange={(e) => setKuaishouConf({ ...kuaishouConf, account_id: e.target.value })}
                    placeholder="如：123456789"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>在快手广告后台「账户信息」中获取</p>
                </div>

                {/* Token */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    监测密钥 / Token <span style={{ color: "#e74c3c" }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showKuaishouToken ? "text" : "password"}
                      value={kuaishouConf.token}
                      onChange={(e) => setKuaishouConf({ ...kuaishouConf, token: e.target.value })}
                      placeholder="请输入快手广告监测密钥"
                      className="w-full rounded-lg px-4 py-2.5 text-sm outline-none pr-10"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKuaishouToken(!showKuaishouToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer"
                    >
                      <i className={showKuaishouToken ? "ri-eye-off-line" : "ri-eye-line"} style={{ color: "#e8b84b", fontSize: "14px" }} />
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>在快手广告后台「工具」→「转化追踪」→「新建转化」→「API回传」中获取</p>
                </div>

                {/* 默认回调URL */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    默认回调URL（可选）
                  </label>
                  <input
                    type="text"
                    value={kuaishouConf.callback_url}
                    onChange={(e) => setKuaishouConf({ ...kuaishouConf, callback_url: e.target.value })}
                    placeholder="https://ad.e.kuaishou.com/rest/atm/callback?..."
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>如果广告链接中未携带callback，将使用此默认地址</p>
                </div>

                {/* 转化事件类型 */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "#d4a96a" }}>
                    转化事件类型
                  </label>
                  <div className="flex gap-3">
                    {[
                      { val: "FORM_SUBMIT", label: "表单提交" },
                      { val: "PURCHASE", label: "付费成交" },
                      { val: "ACTIVATE", label: "激活" },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setKuaishouConf({ ...kuaishouConf, event_type: opt.val })}
                        className="flex-1 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                        style={{
                          border: "1.5px solid rgba(200,146,42,0.4)",
                          background: kuaishouConf.event_type === opt.val ? "linear-gradient(135deg, #c8922a, #e8b84b)" : "rgba(255,255,255,0.05)",
                          color: kuaishouConf.event_type === opt.val ? "#fff" : "#d4a96a",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs mt-1" style={{ color: "#5a4020" }}>
                    当前选择：<strong style={{ color: "#e8b84b" }}>
                      {kuaishouConf.event_type === "PURCHASE" ? "付费成交" : 
                       kuaishouConf.event_type === "FORM_SUBMIT" ? "表单提交" : "激活"}
                    </strong>
                  </p>
                </div>

                {/* 使用说明 */}
                <div className="rounded-lg px-4 py-3 mt-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,146,42,0.2)" }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: "#e8b84b" }}>快手广告投放配置步骤</p>
                  <ol className="text-xs space-y-1.5 ml-4 list-decimal" style={{ color: "#9a7a5a" }}>
                    <li>在快手广告后台创建广告计划，选择「落地页」推广目标</li>
                    <li>在「监测方式」中选择「API回传」</li>
                    <li>复制快手提供的 callback URL（包含监测参数）</li>
                    <li>将 callback URL 填入下方「广告落地页链接」中（追加到URL参数）</li>
                    <li>用户从广告点击后，callback 参数会自动传递到支付成功页面</li>
                    <li>支付成功后，系统会自动调用 callback URL 上报转化</li>
                  </ol>
                </div>

                {/* 转化宝回调地址 */}
                <div className="rounded-lg px-4 py-4" style={{ background: "rgba(255,100,0,0.06)", border: "1.5px solid rgba(255,100,0,0.3)" }}>
                  <p className="text-xs font-bold mb-2" style={{ color: "#ff6400" }}>转化宝回调地址（填入转化宝后台）</p>
                  <div 
                    className="rounded-lg px-3 py-2 text-xs break-all cursor-pointer mb-2"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,100,0,0.2)", color: "#ffaa77", fontFamily: "monospace" }}
                    onClick={() => {
                      const cbUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/zhuanhuabao-notify`;
                      navigator.clipboard.writeText(cbUrl);
                      alert("转化宝回调地址已复制！");
                    }}
                  >
                    {`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/zhuanhuabao-notify`}
                  </div>
                  <p className="text-xs" style={{ color: "#9a7a5a" }}>点击复制 → 填入转化宝后台「回调地址」。用户添加微信后，转化宝会自动调用此地址，系统就会自动上报快手转化</p>
                </div>

                {/* 保存按钮 */}
                <div className="flex items-center gap-4 pt-2">
                  <button
                    onClick={saveKuaishouConf}
                    disabled={kuaishouSaving}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
                    style={{ background: kuaishouSaving ? "rgba(200,146,42,0.3)" : "linear-gradient(135deg, #c8922a, #e8b84b)", color: "#fff" }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      {kuaishouSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                    </div>
                    {kuaishouSaving ? "保存中..." : "保存配置"}
                  </button>
                  {kuaishouMsg && (
                    <p className="text-sm" style={{ color: kuaishouMsg.includes("成功") ? "#27ae60" : "#e74c3c" }}>
                      {kuaishouMsg.includes("成功") ? "✓ " : "✗ "}{kuaishouMsg}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 订单详情弹窗 */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setSelectedOrder(null)}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)", border: "3px solid #c8922a" }} onClick={(e) => e.stopPropagation()}>
            <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold" style={{ color: "#8b1a1a" }}>订单详情</h2>
                <button onClick={() => setSelectedOrder(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: "rgba(200,146,42,0.15)" }}>
                  <i className="ri-close-line" style={{ color: "#c8922a" }} />
                </button>
              </div>
              <div className="space-y-3">
                {[
                  { label: "订单号", value: selectedOrder.order_no },
                  { label: "姓名", value: selectedOrder.name || "—" },
                  { label: "性别", value: selectedOrder.gender || "—" },
                  { label: "生日", value: selectedOrder.birthday || "—" },
                  { label: "金额", value: `¥${selectedOrder.amount?.toFixed(2)}` },
                  { label: "支付状态", value: selectedOrder.status === "paid" ? "✓ 已支付" : "待支付" },
                  { label: "提交时间", value: formatDate(selectedOrder.created_at) },
                  { label: "支付时间", value: selectedOrder.paid_at ? formatDate(selectedOrder.paid_at) : "—" },
                ].map((item) => (
                  <div key={item.label} className="flex gap-3">
                    <span className="text-sm font-semibold whitespace-nowrap w-20 text-right" style={{ color: "#8b1a1a" }}>{item.label}：</span>
                    <span className="text-sm flex-1" style={{ color: "#4a3000" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
          </div>
        </div>
      )}

      {/* 提交记录详情弹窗 */}
      {selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setSelectedRow(null)}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)", border: "3px solid #c8922a" }} onClick={(e) => e.stopPropagation()}>
            <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold" style={{ color: "#8b1a1a" }}>提交详情</h2>
                <button onClick={() => setSelectedRow(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: "rgba(200,146,42,0.15)" }}>
                  <i className="ri-close-line" style={{ color: "#c8922a" }} />
                </button>
              </div>
              <div className="space-y-3">
                {[
                  { label: "姓名", value: selectedRow.name || "—" },
                  { label: "性别", value: selectedRow.gender },
                  { label: "生日", value: selectedRow.birthday },
                  { label: "提交时间", value: formatDate(selectedRow.created_at) },
                ].map((item) => (
                  <div key={item.label} className="flex gap-3">
                    <span className="text-sm font-semibold whitespace-nowrap w-20 text-right" style={{ color: "#8b1a1a" }}>{item.label}：</span>
                    <span className="text-sm flex-1" style={{ color: "#4a3000" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
          </div>
        </div>
      )}
    </div>
  );
}